import { useMsal } from '@azure/msal-react'
import {
  Button,
  Caption1,
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Label,
} from '@fluentui/react-components'
import {
  ArrowUpload24Regular,
  Document20Regular,
} from '@fluentui/react-icons'
import { useRef, useState } from 'react'

import { useAuth } from '../../../../../hooks/useAuth'
import type { UploadWorkflow } from '../../../../../shared/models/UploadPolicy'
import type { WorkspaceItem, WorkspaceUploadProgress } from '../../../../../shared/models/WorkspaceItem'
import { useUploadPolicy } from '../../../../../shared/queries/useUploadPolicy'
import { useUploadWorkspaceFileChunk } from '../../../../../shared/queries/useWorkspaceMediaItems'
import { useWorkspace } from '../../../../../shared/queries/useWorkspaces'
import { getUploadAccept, resolveUploadFormat } from '../../../../../utils/fileWorksapceUploadUtil'
import { uploadWorkspaceFiles } from '../../../../../utils/uploadWorkspaceFiles'
import { canWriteWorkspace } from '../../../../../utils/workspacePermissions'
import WorkspaceUploadStatus from '../WorkspaceUploadStatus'

interface WorkspaceExplorerUploadProps {
  workspaceId: string
  parentId: string | null
  policyWorkflow?: UploadWorkflow,
  onConfirm?: (data: { workspaceId: string; selectedItem?: WorkspaceItem, statusRequest?: boolean }) => void
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    minHeight: '180px',
    padding: tokens.spacingVerticalXL + ' ' + tokens.spacingHorizontalXL,
    border: tokens.strokeWidthThick + ' dashed ' + tokens.colorNeutralStroke1,
    // ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    // ...shorthands.border(tokens.strokeWidthThick, 'dashed', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    cursor: 'pointer',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  dragging: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  icon: { fontSize: '40px' },
  hiddenInput: { display: 'none' },
  list: {
    listStyleType: 'none',
    margin: tokens.spacingVerticalM + ' 0 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS + ' ' + tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  fileName: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadStatus: {
    marginTop: tokens.spacingVerticalXL,
    padding: tokens.spacingVerticalM + ' ' + tokens.spacingHorizontalM,
  },
})

export interface DropZoneProps {
  accept?: string 
  multiple?: boolean
  maxSize?: number 
  disabled?: boolean
  onFilesChange: (files: File[]) => void
}

const formatSize = (b: number) =>
  b < 1024
    ? `${b} B`
    : b < 1048576
      ? `${(b / 1024).toFixed(1)} KB`
      : `${(b / 1048576).toFixed(1)} MB`

export default function WorkspaceExplorerUpload({
  overrideMode = false,
  workspaceId,
  parentId,
  policyWorkflow = 'workspaceMedia',
  onConfirm,
}: WorkspaceExplorerUploadProps & { onConfirm?: (data: { workspaceId: string; selectedItem?: WorkspaceItem, statusRequest?: boolean }) => void } & { overrideMode?: boolean }) {
  const uploadInFlight = useRef(false)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState<WorkspaceUploadProgress | null>(null)
  const { accounts } = useMsal()
  const { isAdmin } = useAuth()
  const { data: workspace } = useWorkspace(workspaceId)
  const {
    data: policy,
    isError: policyError,
    refetch: refetchPolicy,
  } = useUploadPolicy(policyWorkflow)
  const uploadMutation = useUploadWorkspaceFileChunk()
  const canWrite = canWriteWorkspace(workspace, accounts[0]?.username, isAdmin)
  const disabled = !canWrite || !policy || policyError || isUploading

  const styles = useStyles()
  const inputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0) // evita il flicker di dragleave sui figli
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const multiple = false

  

  const onFilesChange = async (files: File[]) => {
  if (!policy || files.length === 0 || uploadInFlight.current) return

  uploadInFlight.current = true
  setIsUploading(true)
  setProgress(null)

  let selectedItem: WorkspaceItem | undefined
  let statusRequest = false

  try {
    const uploadedItems = await uploadWorkspaceFiles({
      files,
      policy,
      workspaceId,
      parentId,
      uploadChunk: uploadMutation.mutateAsync,
      onProgress: setProgress,
      allowDuplicate: overrideMode,
    })

    selectedItem = uploadedItems[0]
    statusRequest = true
  } catch (error) {
    console.error('Workspace upload failed', error)
  } finally {
    uploadInFlight.current = false
    setIsUploading(false)
  }

  onConfirm?.({ workspaceId, selectedItem, statusRequest })
}

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || disabled) return
    const list = Array.from(incoming)
    const valid = list.filter(
      (file) =>
        file.size <= policy.maxFileSizeBytes &&
        resolveUploadFormat(file.name, file.type, policy) !== null,
    )
    setError(
      valid.length < list.length
        ? 'Some files were rejected (type or size not allowed)'
        : null,
    )
    const next =  [...valid]
    setFiles(next)
    onFilesChange(next)
  }

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault() 
    e.stopPropagation()
    e.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragging(false)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    addFiles(e.dataTransfer.files)
  }

  const openPicker = () => !disabled && inputRef.current?.click()

  return (
    <>
      <div>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label="Area di caricamento file"
          className={mergeClasses(
            styles.root,
            isDragging && styles.dragging,
            disabled && styles.disabled,
          )}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openPicker()
            }
          }}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <ArrowUpload24Regular className={styles.icon} />
          <Label weight="semibold">
            Drop file here
          </Label>
          <Caption1>or</Caption1>
          <Button
            appearance="primary"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              openPicker()
            }}
          >
            Select File
          </Button>
          
          <input
            ref={inputRef}
            type="file"
            className={styles.hiddenInput}
            accept={getUploadAccept(policy)}
            multiple={multiple}
            onChange={(e) => {
              addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>

        {error && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Caption1>}

        {files.length > 0 && (
          <ul className={styles.list}>
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`} className={styles.item}>
                <Document20Regular />
                <Label className={styles.fileName}>{file.name}</Label>
                <Caption1>{formatSize(file.size)}</Caption1>
               
              </li>
            ))}
          </ul>
        )}
        </div>
        <div className={styles.uploadStatus}>
          <WorkspaceUploadStatus
          
            progress={progress}
            policyError={policyError}
            onRetryPolicy={() => {
              void refetchPolicy()
            }}
            onDismiss={() => {
              setProgress(null)
            }}
          />
      </div>
    </>
  )
}
